import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { Divider } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import {
  EventDetailsButton,
  EventDetailsContainer,
  EventDetailsDescription,
  EventDetailsGrid,
  EventDetailsGridTitle,
  EventDetailsTitle,
  EventDetailsTitleAndButtonContainer,
  NamedOffenderHeader,
} from 'src/components/organisms/EventDetailsAccordion/EventDetailsAccordion.styles';
import { formatOffenders } from 'src/components/organisms/EventDetailsAccordion/formatOffenders';
import { toggleShowAddNamedOffenderModal } from 'src/components/pages/Case/Case.slice';
import { FormattedNamedOffender } from 'src/components/pages/Case/helpers/namedOffender';
import { getReportNamedOffenders } from 'src/components/pages/Case/helpers/namedOffender/getNamedOffenders/getNamedOffenders';
import { ReportType } from 'src/graphql';
import { useIsFeatureEnabled } from 'src/hooks';
import { TRootState } from 'src/store';
import { FeatureFlag } from 'src/types';

interface DqNamedOffendersSectionProps {
  reportId: string;
}

export const DqNamedOffendersSection = ({
  reportId,
}: DqNamedOffendersSectionProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const addNamedOffenderEnabled = useIsFeatureEnabled(
    FeatureFlag.ADD_NAMED_OFFENDER,
  );
  const useNormalizedDataEnabled = useIsFeatureEnabled(
    FeatureFlag.USE_NORMALIZED_REPORT_DATA,
  );

  const report = useSelector(
    (state: TRootState) =>
      state.case.caseById?.Reports?.find((r) => r.id === reportId) as
        | ReportType
        | undefined,
  );

  const canAddNamedOffender = Boolean(
    report?.AssociatedPermissions?.canAddNamedOffender,
  );
  const canViewReportDetailsPii = Boolean(
    report?.AssociatedPermissions?.canViewReportDetailsPii,
  );
  const showAddNamedOffenderButton =
    addNamedOffenderEnabled && canAddNamedOffender;

  const eventId = report?.ReportEvents?.[0]?.id ?? '';

  const namedOffenders = useMemo(() => {
    if (!report) return [];
    return getReportNamedOffenders(report, useNormalizedDataEnabled);
  }, [report, useNormalizedDataEnabled]);

  const visibleOffenders = canViewReportDetailsPii ? namedOffenders : [];

  const caseById = useSelector((state: TRootState) => state.case.caseById);

  const usersToExcludeInNamedOffenderModal = useMemo(() => {
    const internalOffenderIds = namedOffenders
      .filter((o) => !o.external)
      .map((o) => Number(o.id));

    const caseTeamIds = caseById?.CaseTeam?.map((m) => m.id) ?? [];
    const caseLeadId = caseById?.CaseLead?.id;
    const reporterId = report?.Reporter?.id;
    const assigneeId = report?.Assignee?.id;

    return [
      ...internalOffenderIds,
      ...caseTeamIds,
      caseLeadId,
      reporterId,
      assigneeId,
    ].filter((id): id is number => id !== undefined);
  }, [namedOffenders, caseById, report]);

  const linkOffender = (offender: FormattedNamedOffender, canAdd?: boolean) => {
    if (!addNamedOffenderEnabled || !canAdd || !offender.external) {
      return '-';
    }

    return (
      <EventDetailsButton
        size="small"
        variant="secondary"
        data-testid={`dq-named-offenders-${offender.name}-link-offender`}
        onClick={() => {
          dispatch(
            toggleShowAddNamedOffenderModal({
              open: true,
              usersToExclude: usersToExcludeInNamedOffenderModal,
              reportId,
              eventId,
              externalNamedOffenderToLink: offender.id,
            }),
          );
        }}
      >
        <StyledIcon size="16px" className="uil uil-link-add" />
        Link employee
      </EventDetailsButton>
    );
  };

  if (!eventId || caseById?.sanitizedAt != null) {
    return null;
  }

  return (
    <>
      <Divider variant="horizontal" />
      <EventDetailsContainer
        first
        last
        data-testid="dq-named-offenders-section"
      >
        <EventDetailsTitleAndButtonContainer>
          <NamedOffenderHeader>
            <EventDetailsTitle>Named offender</EventDetailsTitle>
            {showAddNamedOffenderButton && (
              <EventDetailsButton
                size="small"
                variant="primary"
                data-testid="dq-add-named-offender"
                onClick={() => {
                  dispatch(
                    toggleShowAddNamedOffenderModal({
                      open: true,
                      usersToExclude: usersToExcludeInNamedOffenderModal,
                      reportId,
                      eventId,
                    }),
                  );
                }}
              >
                <StyledIcon size="16px" className="uil uil-plus" />
                Named offender
              </EventDetailsButton>
            )}
          </NamedOffenderHeader>
        </EventDetailsTitleAndButtonContainer>
        <EventDetailsGrid>
          <EventDetailsGridTitle>NAME</EventDetailsGridTitle>
          <EventDetailsGridTitle>DEPARTMENT</EventDetailsGridTitle>
          <EventDetailsGridTitle>SITE</EventDetailsGridTitle>
          <EventDetailsGridTitle>COUNTRY</EventDetailsGridTitle>
          <EventDetailsGridTitle>REPORT ID</EventDetailsGridTitle>
          <EventDetailsGridTitle>ADDED</EventDetailsGridTitle>
          <EventDetailsGridTitle>LINKED</EventDetailsGridTitle>
          {visibleOffenders.length === 0 ? (
            <EventDetailsDescription
              data-testid="dq-no-named-offenders"
              fullWidth
            >
              -
            </EventDetailsDescription>
          ) : (
            formatOffenders({
              namedOffenders: visibleOffenders,
              dataTestId: 'dq-named-offenders',
              linkOffender,
              canAddNamedOffender,
              theme,
            })
          )}
        </EventDetailsGrid>
      </EventDetailsContainer>
      <Divider variant="horizontal" />
    </>
  );
};
